import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { MaterialDoc, ObservationChecklist, FamilyLetter } from "../../../../api/serving";
import { docKeyLabel, peT } from "../../../../i18n/peLesson";
import "./materialsPrint.css";

/**
 * Shared teacher-material renderers (Run E3 B2).
 *
 * Extracted from SceneReader so the same guide / quick reference / checklist /
 * family letter views work in the lesson preview AND in the materials rail on the
 * signed-in curriculum chapter page. Every panel also renders a print-only copy
 * (portal to <body>) so Print gives a clean letter page with no app chrome.
 */

/** Source rows still carry build placeholders ("(4th launch word)", "artifact gap", "WP-4"). Never print them. */
const PLACEHOLDER = /artifact gap|launch word|pending WP|\u26A0/i;
const isPlaceholderItem = (v: any): boolean =>
  typeof v === "string"
    ? PLACEHOLDER.test(v)
    : v && typeof v === "object" && !Array.isArray(v) && Object.values(v).some((x) => typeof x === "string" && PLACEHOLDER.test(x));

/** Drop placeholder entries from a comma list that follows a colon, e.g. "using these words: a, (4th launch word)." */
export function dropPlaceholderWords(text: string): string {
  return text.replace(/(:\s)([^.:]*?)(\.)(?=\s|$)/g, (m, colon, list, dot) => {
    if (!PLACEHOLDER.test(list)) return m;
    const kept = list.split(",").map((x: string) => x.trim()).filter((x: string) => x && !PLACEHOLDER.test(x));
    return colon + kept.join(", ") + dot;
  });
}

/** Generic renderer for a structured material doc (see SceneReader history). */
export const StructuredDoc: React.FC<{ value: any; depth?: number; lang: string }> = ({ value, depth = 0, lang }) => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "string") {
    return (
      <div className="prose prose-sm max-w-none text-gray-700">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
      </div>
    );
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return <span className="text-sm text-gray-700">{String(value)}</span>;
  }
  if (Array.isArray(value)) {
    value = value.filter((v: any) => !isPlaceholderItem(v));
    if (value.length === 0) return null;
    const allPrimitive = value.every((v: any) => typeof v !== "object" || v === null);
    if (allPrimitive) {
      return (
        <ul className="list-disc list-outside ml-5 space-y-0.5 marker:text-gray-300">
          {value.map((v: any, i: number) => (
            <li key={i} className="text-sm text-gray-700">{String(v)}</li>
          ))}
        </ul>
      );
    }
    return (
      <div className="space-y-2">
        {value.map((v: any, i: number) => (
          <div key={i} className="pe-keep rounded-md border border-gray-200 p-2.5">
            <StructuredDoc value={v} depth={depth + 1} lang={lang} />
          </div>
        ))}
      </div>
    );
  }
  // object
  const entries = Object.entries(value).filter(
    ([k, v]) => !k.startsWith("_") && v !== null && v !== undefined && v !== "",
  );
  if (entries.length === 0) return null;
  return (
    <div className={depth === 0 ? "space-y-4" : "space-y-2"}>
      {entries.map(([k, v]) => (
        <section key={k} className="pe-keep">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            {docKeyLabel(lang, k)}
          </h4>
          <StructuredDoc value={v} depth={depth + 1} lang={lang} />
        </section>
      ))}
    </div>
  );
};

interface PrintPath {
  title?: string;
  intro?: string;
  read_the_big_book?: string;
  paper_practice?: string;
  paper_extra?: string;
  turn_in?: { k2?: string; g35?: string };
}

/**
 * The "print path" box (Run E3 B2, change 4): how to run the lesson from paper.
 * Data is content_json.print_path on the teacher guide and the quick reference.
 * Shown first, above the rest of the doc. The video steps below are unchanged.
 */
export const PrintPathBox: React.FC<{ value: PrintPath; lang: string }> = ({ value, lang }) => {
  const t = peT(lang);
  const turn = value.turn_in ?? {};
  return (
    <aside
      className="pe-keep pe-print-path rounded-lg border-2 border-emerald-600 bg-emerald-50 p-4"
      aria-label={value.title ?? t.docKeys.print_path}
      data-testid="print-path-box"
    >
      <h3 className="text-sm font-bold text-emerald-900">{value.title ?? t.docKeys.print_path}</h3>
      {value.intro && <p className="mt-1 text-sm text-emerald-900">{value.intro}</p>}
      <ul className="mt-3 space-y-2 text-sm text-gray-800">
        {value.read_the_big_book && <li>{value.read_the_big_book}</li>}
        {value.paper_practice && <li>{dropPlaceholderWords(value.paper_practice)}</li>}
        {value.paper_extra && <li>{value.paper_extra}</li>}
        {turn.k2 && <li>{turn.k2}</li>}
        {turn.g35 && <li>{turn.g35}</li>}
      </ul>
    </aside>
  );
};

/** Marks <body> while a printable panel is on screen, so print CSS can hide the app. */
function usePrintingBody() {
  useEffect(() => {
    document.body.classList.add("pe-printing");
    return () => document.body.classList.remove("pe-printing");
  }, []);
}

/** Print-only copy of the panel content, rendered at the end of <body>. */
export const PrintCopy: React.FC<{ heading?: string; children: React.ReactNode }> = ({ heading, children }) => {
  usePrintingBody();
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="pe-print-root" data-testid="print-copy">
      {heading && <p className="pe-print-heading">{heading}</p>}
      {children}
    </div>,
    document.body,
  );
};

export const PrintButton: React.FC<{ lang: string }> = ({ lang }) => {
  const t = peT(lang).materialsRail;
  return (
    <button
      type="button"
      onClick={() => window.print()}
      className="pe-noprint inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-800 hover:bg-gray-50"
      title={t.printHint}
    >
      <span aria-hidden="true">&#128424;</span>
      {t.print}
    </button>
  );
};

/** Pulls `print_path` out of a doc so the box can lead and the rest renders generically. */
function splitPrintPath(content: any): { pp: PrintPath | null; rest: any } {
  if (content && typeof content === "object" && !Array.isArray(content) && content.print_path) {
    const { print_path, ...rest } = content;
    return { pp: print_path as PrintPath, rest };
  }
  return { pp: null, rest: content };
}

/** Optional "For Tier N" callout (guide: differentiation[tier]; quick reference: tier_outputs). */
function tierNote(content: any, tier?: string): string | null {
  if (!tier || !content || typeof content !== "object") return null;
  const d = content.differentiation?.[tier];
  if (typeof d === "string" && d) return d;
  const o = Array.isArray(content.tier_outputs) ? content.tier_outputs.find((x: any) => x?.tier === tier) : null;
  return typeof o?.output === "string" && o.output ? o.output : null;
}

/** A doc-hub material doc body: print path box, optional tier callout, generic render, print copy. */
export const MaterialDocPanel: React.FC<{
  doc: MaterialDoc | null | undefined;
  empty: string;
  lang: string;
  tier?: string;
  /** Short heading printed above the print copy, e.g. "Project Explore, Chapter 3, Tier 2, EN". */
  printHeading?: string;
  showPrintButton?: boolean;
}> = ({ doc, empty, lang, tier, printHeading, showPrintButton }) => {
  const t = peT(lang);
  if (!doc) {
    return <p className="text-sm text-gray-500">{empty}</p>;
  }
  const { pp, rest } = splitPrintPath(doc.content_json);
  const note = tierNote(doc.content_json, tier);
  const body = (
    <>
      {pp && <PrintPathBox value={pp} lang={lang} />}
      {note && tier && (
        <div className="pe-keep rounded-md border border-indigo-200 bg-indigo-50 p-3">
          <p className="text-xs font-bold uppercase tracking-wide text-indigo-700">{t.materialsRail.forTier(t.tier(tier))}</p>
          <p className="mt-1 text-sm text-gray-800">{note}</p>
        </div>
      )}
      <StructuredDoc value={rest} lang={lang} />
    </>
  );
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">{doc.title}</h3>
        {showPrintButton && <PrintButton lang={lang} />}
      </div>
      <div className="space-y-4">{body}</div>
      <PrintCopy heading={printHeading}>
        <h1 className="pe-print-title">{doc.title}</h1>
        <div className="space-y-4">{body}</div>
      </PrintCopy>
    </section>
  );
};

/** Observation checklist body (same markup the lesson-preview tab used). */
export const ChecklistPanel: React.FC<{
  checklist: ObservationChecklist | null | undefined;
  empty: string;
  lang: string;
  printHeading?: string;
  showPrintButton?: boolean;
}> = ({ checklist, empty, lang, printHeading, showPrintButton }) => {
  const t = peT(lang);
  if (!checklist) return <p className="text-sm text-gray-500">{empty}</p>;
  const list = (
    <ul className="space-y-3">
      {checklist.behaviors.map((b, i) => (
        <li key={i} className="pe-keep rounded-md border border-gray-200 p-3">
          <div className="flex gap-2">
            <span className="text-indigo-600 mt-0.5">✓</span>
            <p className="text-sm font-medium text-gray-900">
              {b.text}
              {b.scene_ref > 0 && (
                <span className="ml-1 text-xs font-normal text-gray-400">({t.sceneRef(b.scene_ref)})</span>
              )}
            </p>
          </div>
          {b.look_fors && b.look_fors.length > 0 && (
            <ul className="mt-2 ml-6 space-y-1 list-disc list-outside marker:text-gray-300">
              {b.look_fors.map((lf, li) => (
                <li key={li} className="text-xs text-gray-600">{lf}</li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">{checklist.title}</h3>
        {showPrintButton && <PrintButton lang={lang} />}
      </div>
      {list}
      <PrintCopy heading={printHeading}>
        <h1 className="pe-print-title">{checklist.title}</h1>
        {list}
      </PrintCopy>
    </section>
  );
};

/** Family letter body. The data may still carry a front matter block; never show it. */
const FRONT_MATTER = /^\s*---\n[\s\S]*?\n---\n?/;
export const FamilyLetterPanel: React.FC<{
  letter: FamilyLetter | null | undefined;
  empty: string;
  lang: string;
  printHeading?: string;
  showPrintButton?: boolean;
}> = ({ letter, empty, lang, printHeading, showPrintButton }) => {
  if (!letter) return <p className="text-sm text-gray-500">{empty}</p>;
  const body = (letter.body ?? "").replace(FRONT_MATTER, "").trim();
  const md = (
    <div className="prose prose-sm max-w-none text-gray-700">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
    </div>
  );
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">{letter.title}</h3>
        {showPrintButton && <PrintButton lang={lang} />}
      </div>
      {md}
      <PrintCopy heading={printHeading}>
        <h1 className="pe-print-title">{letter.title}</h1>
        {md}
      </PrintCopy>
    </section>
  );
};
